import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  Eye,
  EyeOff,
  Users as UsersIcon,
  UserPlus,
  DollarSign,
  FileText,
  Download,
  Trash2,
  Plus,
} from "lucide-react";
import PageShell from "../dashboard/PageShell";

const readOnlyCell = (label: string, value?: React.ReactNode) => (
  <tr>
    <td className="px-4 py-2 text-sm text-gray-700 w-64 align-top bg-gray-50 border-b border-gray-200">
      {label}
    </td>
    <td className="px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
      {value ?? <span className="text-gray-400">-</span>}
    </td>
  </tr>
);

const ProjectDetail: React.FC = () => {
  const navigate = useNavigate();
  const id = useMemo(() => {
    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      return parts[parts.length - 1] || "";
    } catch {
      return "";
    }
  }, []);
  const [activeTab, setActiveTab] = useState(() => {
    // Load active tab from localStorage, default to "topic-info"
    return localStorage.getItem("projectDetailActiveTab") || "topic-info";
  });

  const data = useMemo(
    () => ({
      topicName: "Nghiên cứu phần mềm quản lý KHCN có ứng dụng AI",
      managementLevel: "Cấp Viện/ Chi nhánh",
      topicType: "Đề tài",
      leadUnit: "Trung tâm Đào tạo và Hợp tác Quốc tế",
      chair: "Thượng tá, VS. ThS Lê Anh Tuấn",
      secretary: "Thượng tá, GS. TS Nguyễn Thị Hạnh",
      researchField: "Công nghệ thông tin và tự động hóa",
      duration: "12/2025 - 12/2025",
      provinceCity: "Hà Nội",
      proposedBudget: "10.000.000.000 đ (10 tỷ)",
      objective:
        "Xây dựng phần mềm phục vụ quản trị KHCN tại đơn vị gồm 1000 nghiên cứu viên",
      novelty: "Mới",
      mainContent:
        "Phần mềm triển khai trong toàn đơn vị với dự kiến 25 đề tài/năm",
      application: "Ứng dụng cấp đơn vị",
      expectedEffect: "Ứng dụng cho mạng lưới",
    }),
    []
  );
  const [showDetails, setShowDetails] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddAttachment, setShowAddAttachment] = useState(false);
  const [showEditAttachment, setShowEditAttachment] = useState(false);
  const [editingAttachmentIndex, setEditingAttachmentIndex] = useState<
    number | null
  >(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState<
    "member" | "progress" | "attachment" | "funding"
  >("member");
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [memberForm, setMemberForm] = useState({
    selectedMember: "",
    fullName: "",
    phone: "",
    email: "",
    unit: "",
    position: "",
    role: "",
    work: "",
  });
  const [attachmentForm, setAttachmentForm] = useState({
    name: "",
    file: null as File | null,
  });
  const handleMemberChange = (field: string, value: string) => {
    setMemberForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAttachmentChange = (
    field: string,
    value: string | File | null
  ) => {
    setAttachmentForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditAttachment = (index: number) => {
    setEditingAttachmentIndex(index);
    setShowEditAttachment(true);
  };

  const handleSaveAttachment = () => {
    console.log("Save attachment", attachmentForm);
    setShowAddAttachment(false);
    setShowEditAttachment(false);
    setEditingAttachmentIndex(null);
    setAttachmentForm({ name: "", file: null });
  };

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
    localStorage.setItem("projectDetailActiveTab", tabKey);
  };

  const handleDeleteClick = (
    type: "member" | "progress" | "attachment" | "funding",
    index: number
  ) => {
    setDeleteType(type);
    setDeleteIndex(index);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    console.log(`Delete ${deleteType} at index ${deleteIndex}`);
    setShowDeleteConfirm(false);
    setDeleteType("member");
    setDeleteIndex(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteType("member");
    setDeleteIndex(null);
  };

  return (
    <PageShell title="Chi tiết đề tài">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => window.history.back()}
              aria-label="Go back"
              className="p-3 rounded-full text-[#337ab7] bg-blue-50 hover:bg-blue-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-800 truncate">
              Chi tiết đề tài
              <span className="ml-2 text-sm font-normal text-gray-600 truncate align-middle">
                – {data.topicName}
              </span>
            </h1>
          </div>
          <button
            onClick={() => navigate(`/topics/approval/detail/${id}/edit`)}
            className="bg-[#337ab7] text-white px-4 py-2 rounded text-sm font-medium hover:bg-[#2e6da4] transition-colors flex items-center space-x-2 justify-center min-w-[110px]"
          >
            <Pencil className="h-4 w-4" />
            <span>Sửa</span>
          </button>
        </div>

        <div className="bg-white rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6 overflow-x-auto">
              {[
                { key: "topic-info", label: "Thông tin đề tài" },
                { key: "members", label: "Thành viên tham gia" },
                { key: "full-content", label: "Nội dung đầy đủ" },
                { key: "attachments", label: "Tài liệu đính kèm" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => handleTabChange(t.key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === t.key
                      ? "border-[#337ab7] text-[#337ab7]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === "topic-info" && (
              <div className="space-y-6">
                <table className="w-full border border-gray-200 rounded-md overflow-hidden">
                  <tbody>
                    {readOnlyCell("Tên đề tài", data.topicName)}
                    {readOnlyCell("Cấp quản lý", data.managementLevel)}
                    {readOnlyCell("Dạng đề tài", data.topicType)}
                    {readOnlyCell("Đơn vị chủ trì thực hiện", data.leadUnit)}
                    {readOnlyCell("Chủ nhiệm đề tài", data.chair)}
                    {readOnlyCell("Thư ký đề tài", data.secretary)}
                    {readOnlyCell("Lĩnh vực nghiên cứu", data.researchField)}
                    {readOnlyCell("Thời gian thực hiện", data.duration)}
                    {readOnlyCell("Tỉnh / Thành phố", data.provinceCity)}
                    {readOnlyCell("Kinh phí đề xuất", data.proposedBudget)}
                  </tbody>
                </table>

                <div className="mb-2">
                  <button
                    type="button"
                    onClick={() => setShowDetails((v) => !v)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50"
                  >
                    {showDetails ? (
                      <EyeOff className="h-4 w-4 text-[#337ab7]" />
                    ) : (
                      <Eye className="h-4 w-4 text-[#337ab7]" />
                    )}
                    <span>Xem chi tiết thông tin</span>
                  </button>
                </div>
                {showDetails && (
                  <table className="w-full border border-gray-200 rounded-md overflow-hidden">
                    <tbody>
                      {readOnlyCell("Mục tiêu", data.objective)}
                      {readOnlyCell(
                        "Tính cấp thiết và tính mới của nhiệm vụ",
                        data.novelty
                      )}
                      {readOnlyCell("Nội dung chính", data.mainContent)}
                      {readOnlyCell("Kết quả dự kiến", data.expectedEffect)}
                      {readOnlyCell(
                        "Khả năng và địa chỉ ứng dụng",
                        data.application
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === "members" && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <button
                    onClick={() =>
                      navigate(`/topics/approval/detail/${id}/members/add`)
                    }
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm"
                  >
                    <UserPlus className="h-4 w-4" />
                    <span>Thêm thành viên tham gia</span>
                  </button>
                </div>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                            #
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Họ tên
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Chức danh thực hiện
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Điện thoại
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Email
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Đơn vị
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Nội dung công việc
                          </th>
                          <th className="px-3 py-2 text-center text-xs font-bold text-black uppercase tracking-wider w-32">
                            Chức năng
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {/* Example row; replace with real data when available */}
                        <tr className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">
                            1
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            Thượng tá, VS. ThS Lê Anh Tuấn
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200"></td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            0988662866
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            tuan@vnet.vn
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            Viện Nước, Tưới Tiêu và Môi trường
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200"></td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex justify-center space-x-4">
                              <button
                                className="text-[#337ab7] hover:underline text-lg"
                                title="Sửa"
                              >
                                ✎
                              </button>
                              <button
                                onClick={() => handleDeleteClick("member", 0)}
                                className="text-red-600 hover:underline text-lg"
                                title="Xóa"
                              >
                                🗑
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex justify-between items-center p-4 border-t border-gray-200">
                    <div className="text-sm font-medium text-gray-700">
                      Hiển thị <span className="font-bold">1</span> đến{" "}
                      <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                        <option>10</option>
                        <option>20</option>
                        <option>50</option>
                        <option>100</option>
                      </select>{" "}
                      trong <span className="font-bold">10000</span> kết quả
                    </div>
                    <div className="flex space-x-2">
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        &lt;
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        1
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                        2
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        3
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        &gt;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "full-content" && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="text-sm text-gray-700 leading-relaxed space-y-3">
                  <p>
                    Xây dựng phần mềm quản lý KHCN phục vụ 1000 nghiên cứu viên.
                    Hệ thống hỗ trợ quản lý toàn diện các hoạt động nghiên cứu
                    từ đăng ký đề tài, theo dõi tiến độ, quản lý kinh phí đến
                    báo cáo kết quả.
                  </p>

                  <p>
                    Hiện tại việc quản lý KHCN đang thực hiện thủ công, gây khó
                    khăn trong theo dõi và đánh giá. Việc ứng dụng AI sẽ tự động
                    hóa và tối ưu hóa các quy trình quản lý.
                  </p>

                  <p>
                    Đây là lần đầu tiên ứng dụng trí tuệ nhân tạo vào hệ thống
                    quản lý KHCN, với khả năng dự đoán xu hướng nghiên cứu và tự
                    động phân loại các nghiên cứu theo lĩnh vực.
                  </p>

                  <p>
                    Nội dung chính gồm: Phân tích và thiết kế hệ thống, phát
                    triển module quản lý đề tài, tích hợp AI và Machine
                    Learning, triển khai và vận hành hệ thống.
                  </p>

                  <p>
                    Kết quả dự kiến: Hệ thống phần mềm hoàn chỉnh, giảm 70% thời
                    gian xử lý thủ tục hành chính, tăng 50% hiệu quả theo dõi
                    tiến độ, báo cáo khoa học và tài liệu hướng dẫn.
                  </p>

                  <p>
                    Hệ thống sẽ triển khai trong toàn bộ đơn vị, có thể mở rộng
                    cho mạng lưới nghiên cứu và có tiềm năng thương mại hóa.
                  </p>
                </div>
              </div>
            )}

            {false && activeTab === "funding" && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <button
                    onClick={() =>
                      navigate(`/topics/approval/detail/${id}/funding/add`)
                    }
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm"
                  >
                    <DollarSign className="h-4 w-4" />
                    <span>Thêm kinh phí phê duyệt - đã cấp</span>
                  </button>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                            #
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Nguồn kinh phí
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Năm
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Kinh phí phê duyệt
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Kinh phí đã cấp
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Ngày cấp
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Ghi chú
                          </th>
                          <th className="px-3 py-2 text-center text-xs font-bold text-black uppercase tracking-wider w-32">
                            Chức năng
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">
                            1
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            Ngân sách giao tự chủ cho viện
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            2022
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            100.000.000 đ
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            50.000.000 đ
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            15/11/2022
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            —
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex justify-center space-x-4">
                              <button
                                className="text-[#337ab7] hover:underline text-lg"
                                title="Sửa"
                              >
                                ✎
                              </button>
                              <button
                                onClick={() => handleDeleteClick("funding", 0)}
                                className="text-red-600 hover:underline text-lg"
                                title="Xóa"
                              >
                                🗑
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex justify-between items-center p-4 border-t border-gray-200">
                    <div className="text-sm font-medium text-gray-700">
                      Hiển thị <span className="font-bold">1</span> đến{" "}
                      <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                        <option>10</option>
                        <option>20</option>
                        <option>50</option>
                        <option>100</option>
                      </select>{" "}
                      trong <span className="font-bold">10000</span> kết quả
                    </div>
                    <div className="flex space-x-2">
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        &lt;
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        1
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                        2
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        3
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        &gt;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {false && activeTab === "progress" && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <button
                    onClick={() =>
                      navigate(`/topics/approval/detail/${id}/progress/add`)
                    }
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Thêm báo cáo tiến độ</span>
                  </button>
                </div>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            STT
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Tháng
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Nội dung báo cáo
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Kế hoạch tháng tiếp theo
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            File đính kèm
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Ngày tháng
                          </th>
                          <th className="px-3 py-2 text-center text-xs font-bold text-black uppercase tracking-wider w-32">
                            Chức năng
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">
                            1
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            05/2022
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            s
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            a
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            —
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            10/11/2022 23:20
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex justify-center space-x-4">
                              <button
                                className="text-[#337ab7] hover:underline text-lg"
                                title="Sửa"
                              >
                                ✎
                              </button>
                              <button
                                className="text-red-600 hover:underline text-lg"
                                title="Xóa"
                              >
                                🗑
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex justify-between items-center p-4 border-t border-gray-200">
                    <div className="text-sm font-medium text-gray-700">
                      Hiển thị <span className="font-bold">1</span> đến{" "}
                      <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                        <option>10</option>
                        <option>20</option>
                        <option>50</option>
                        <option>100</option>
                      </select>{" "}
                      trong <span className="font-bold">10000</span> kết quả
                    </div>
                    <div className="flex space-x-2">
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        &lt;
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        1
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                        2
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        3
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        &gt;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "attachments" && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowAddAttachment(true)}
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm"
                  >
                    <Plus className="h-4 w-4" />
                    Thêm tài liệu đính kèm của đề tài
                  </button>
                </div>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            STT
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Tên file
                          </th>
                          <th className="px-3 py-2 text-center text-xs font-bold text-black uppercase tracking-wider w-32">
                            Chức năng
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {[
                          "Quyết định Phê duyệt danh mục",
                          "Thuyết minh nhiệm vụ",
                          "Quyết định thành lập HĐXDTMĐC",
                          "Biên bản họp HĐXDTMĐC",
                          "QĐ thành lập HĐTĐKP",
                          "Biên bản họp HĐTĐKP",
                          "Quyết định phê duyệt thực hiện/Hợp đồng NCKH&CN",
                          "Báo cáo định kỳ",
                          "Phê duyệt cấp kinh phí: của các đợt phê duyệt cấp kinh phí",
                        ].map((fileName, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">
                              {index + 1}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                              {fileName}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex justify-center space-x-2">
                                <button
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-1 rounded transition-colors"
                                  title="Tải xuống"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleEditAttachment(index)}
                                  className="text-[#337ab7] hover:text-blue-700 hover:bg-blue-50 p-1 rounded transition-colors"
                                  title="Sửa"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteClick("attachment", index)
                                  }
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                                  title="Xóa"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex justify-between items-center p-4 border-t border-gray-200">
                    <div className="text-sm font-medium text-gray-700">
                      Hiển thị <span className="font-bold">1</span> đến{" "}
                      <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                        <option>10</option>
                        <option>20</option>
                        <option>50</option>
                        <option>100</option>
                      </select>{" "}
                      trong <span className="font-bold">10000</span> kết quả
                    </div>
                    <div className="flex space-x-2">
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        &lt;
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        1
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                        2
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        3
                      </button>
                      <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        &gt;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Attachment Modal */}
      {showAddAttachment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Thêm tài liệu đính kèm
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên tài liệu <span className="text-red-500">(*)</span>
                </label>
                <input
                  type="text"
                  value={attachmentForm.name}
                  onChange={(e) =>
                    handleAttachmentChange("name", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập tên tài liệu..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File đính kèm <span className="text-red-500">(*)</span>
                </label>
                <input
                  type="file"
                  onChange={(e) =>
                    handleAttachmentChange("file", e.target.files?.[0] || null)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowAddAttachment(false);
                  setAttachmentForm({ name: "", file: null });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleSaveAttachment}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium"
              >
                Lưu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Attachment Modal */}
      {showEditAttachment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Sửa tài liệu đính kèm
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên tài liệu <span className="text-red-500">(*)</span>
                </label>
                <input
                  type="text"
                  value={attachmentForm.name}
                  onChange={(e) =>
                    handleAttachmentChange("name", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập tên tài liệu..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File đính kèm
                </label>
                <input
                  type="file"
                  onChange={(e) =>
                    handleAttachmentChange("file", e.target.files?.[0] || null)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowEditAttachment(false);
                  setEditingAttachmentIndex(null);
                  setAttachmentForm({ name: "", file: null });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleSaveAttachment}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium"
              >
                Lưu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-md p-4 max-w-sm w-full mx-4">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              Xác nhận xóa
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              Bạn có chắc muốn xóa{" "}
              {deleteType === "member"
                ? "thành viên"
                : deleteType === "progress"
                ? "báo cáo tiến độ"
                : deleteType === "attachment"
                ? "tài liệu đính kèm"
                : "kinh phí"}
              ?
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={handleDeleteCancel}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default ProjectDetail;
